/**
 * Accessible tabs (WAI-ARIA tabs pattern with automatic activation).
 * Markup: [role=tablist] > [role=tab][aria-controls] and [role=tabpanel] siblings.
 */
class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** @returns {HTMLElement[]} */
  get tabs() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
  }

  /**
   * @param {HTMLElement} tab
   * @param {boolean} [focus]
   */
  select(tab, focus = false) {
    this.tabs.forEach((current) => {
      const selected = current === tab;
      current.setAttribute('aria-selected', String(selected));
      current.tabIndex = selected ? 0 : -1;

      const panelId = current.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (panel instanceof HTMLElement) panel.hidden = !selected;
    });

    if (focus) tab.focus();
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLElement && this.contains(tab)) this.select(tab);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const current = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(current instanceof HTMLElement)) return;

    const tabs = this.tabs;
    const index = tabs.indexOf(current);
    const isRtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;

    switch (event.key) {
      case 'ArrowRight':
        next = isRtl ? index - 1 : index + 1;
        break;
      case 'ArrowLeft':
        next = isRtl ? index + 1 : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const target = tabs[(next + tabs.length) % tabs.length];
    if (target) this.select(target, true);
  };
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
